import * as React from 'react';
import { useMemo, useState } from 'react';
import isEqual from 'lodash/isEqual';
import {
    isForgeCustomFieldsOnPortalRequestEnabled,
    isForgeOnConsentInitializationPropEnabled,
    isUiModificationsForgeContextTokenEndpointEnabled,
} from 'feature-flags';
import { noop } from 'lodash';
import { useIntl } from 'react-intl-next';
import Flag, { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Spinner from '@atlaskit/spinner';
import { B300, G300, R400, Y200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import type { FlagOptions } from '@atlassian/forge-ui/iframe';
import { ForgeUIRenderer } from '@atlassian/forge-ui/ui';
import {
    CUSTOM_FIELD,
    CUSTOM_FIELD_TYPE,
    HELP_CENTER_LAYOUT_ELEMENT_MODULE,
    PORTAL_FOOTER_MODULE,
    PORTAL_HEADER_MODULE,
    PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
    PORTAL_SUBHEADER_MODULE,
    PROFILE_PANEL_MODULE,
    REQUEST_DETAIL_MAIN_SECTION_MODULE,
    REQUEST_DETAIL_SIDE_SECTION_MODULE,
    REQUEST_VIEW_ACTION_MODULE,
    USER_MENU_ACTION_SECTION_MODULE,
    UI_MODIFICATIONS,
} from '@atlassian/help-center-common-component/forge-ui/constants';
import { getApolloClient } from '@atlassian/help-center-common-util/apollo';
import {
    isCustomUIExtension,
    isUIKit2Extension,
    isCustomUIExtensionOld,
    isUIKit2ExtensionOld,
} from '@atlassian/help-center-common-util/forge-ui';
import { getMeta } from '@atlassian/help-center-common-util/meta';
import { getMentionProvider } from '../../mention-provider';
import { fetchFCTOld, fetchFCTNew } from '../context-token';
import type { ExtensionData, ExtensionPayload, ExtensionPointModule, RendererProps, ViewportSizeType } from '../types';
import { UIKit1Renderer } from '../ui-kit-1-renderer';
import { getContextIds } from './utils';

type ViewportSizeTypeWithDefault = ViewportSizeType | 'default';

type ViewportSizeObjectType = {
    [key in ViewportSizeTypeWithDefault]: string;
};

const panelHeights: ViewportSizeObjectType = {
    xsmall: '50px',
    small: '112px',
    medium: '262px',
    default: '100%',
    large: '524px',
    xlarge: '1048px',
};

const extensionPointHeights: { [key in ExtensionPointModule]: ViewportSizeObjectType | void } = {
    [REQUEST_DETAIL_MAIN_SECTION_MODULE]: panelHeights,
    [REQUEST_DETAIL_SIDE_SECTION_MODULE]: panelHeights,
    [PORTAL_FOOTER_MODULE]: panelHeights,
    [PORTAL_HEADER_MODULE]: panelHeights,
    [PORTAL_SUBHEADER_MODULE]: panelHeights,
    [PROFILE_PANEL_MODULE]: panelHeights,
    [USER_MENU_ACTION_SECTION_MODULE]: panelHeights,
    [REQUEST_VIEW_ACTION_MODULE]: panelHeights,
    [PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE]: panelHeights,
    [HELP_CENTER_LAYOUT_ELEMENT_MODULE]: panelHeights,
    [CUSTOM_FIELD]: panelHeights,
    [CUSTOM_FIELD_TYPE]: panelHeights,
    [UI_MODIFICATIONS]: undefined,
};

const calculateHeight = (type: ExtensionPointModule, size?: ViewportSizeType) => {
    const heights = extensionPointHeights[type] ?? panelHeights;
    return heights[size ?? 'default'];
};

const flagIcon = (type?: string) => {
    const successIcon = <SuccessIcon label="Success" primaryColor={token('color.icon.success', G300)} />;
    const infoIcon = <InfoIcon label="Info" primaryColor={token('color.icon.information', B300)} />;
    const warningIcon = <WarningIcon label="Warning" primaryColor={token('color.icon.warning', Y200)} />;
    const errorIcon = <ErrorIcon label="Error" primaryColor={token('color.icon.danger', R400)} />;
    switch (type) {
        case 'info':
            return infoIcon;
        case 'warning':
            return warningIcon;
        case 'error':
            return errorIcon;
        default:
            return successIcon;
    }
};

export const Renderer: React.FC<RendererProps> = ({
    onSubmit,
    extension,
    extensionData,
    extensionPayload,
    getComponents,
    modalExtension,
    entryPoint,
    bridge,
    customBridgeMethods,
    onIframeLoad,
    isIframeHidden,
    onConsentInitialization,
    onConsentSuccess,
    consentMessage,
    contextTokenType,
    loadingComponent = <Spinner size="large" />,
    onRender = noop,
    onModalExtensionClose = noop,
    isIframeResizable = true,
}) => {
    const cloudId = getMeta('tenant-id') || '';
    const accountId = getMeta('account-id') || '';
    const activationId = getMeta('activation-id') || '';
    const contextIds = getContextIds(cloudId, activationId);
    const [extensionDataLocal, setExtensionDataLocal] = React.useState<ExtensionData>(extensionData);
    const [extensionPayloadLocal, setExtensionPayloadLocal] = React.useState<ExtensionPayload | undefined>(
        extensionPayload
    );
    const apolloClient = getApolloClient();
    const { locale } = useIntl();
    const [flags, setFlags] = useState<FlagOptions[]>([]);

    React.useEffect(() => {
        if (!isEqual(extensionData, extensionDataLocal)) {
            setExtensionDataLocal(extensionData);
        }
    }, [extensionData, extensionDataLocal]);

    React.useEffect(() => {
        if (!isEqual(extensionPayload, extensionPayloadLocal)) {
            setExtensionPayloadLocal(extensionPayload);
        }
    }, [extensionPayload, extensionPayloadLocal]);

    const contextTokenCallback = React.useCallback(() => {
        if (isUiModificationsForgeContextTokenEndpointEnabled()) {
            return fetchFCTNew(extension, extensionData, contextTokenType);
        }

        return fetchFCTOld(extension, extensionData);
    }, [extension, extensionData, contextTokenType]);

    const components = useMemo(
        () => (isForgeCustomFieldsOnPortalRequestEnabled() ? getComponents?.({}) : undefined),
        [getComponents]
    );

    const requestKey = React.useMemo(() => {
        if ('request' in extensionDataLocal && extensionDataLocal.request) {
            return extensionDataLocal.request.key || '';
        }
        return '';
    }, [extensionDataLocal]);

    const showFlag = (options?: FlagOptions) => {
        if (options) {
            setFlags((currentFlags) => [options, ...currentFlags]);
        }
        return true;
    };

    const removeFlag = (flagIdToRemove: number | string) => {
        setFlags((current) => current.filter((flag) => flag.id !== flagIdToRemove));
    };

    const closeFlag = (options: Pick<FlagOptions, 'id'>) => {
        if (options.id) {
            removeFlag(options.id);
        }
        return true;
    };

    if (
        isForgeCustomFieldsOnPortalRequestEnabled()
            ? isCustomUIExtension(extension, entryPoint) || isUIKit2Extension(extension, entryPoint)
            : isCustomUIExtensionOld(extension) || isUIKit2ExtensionOld(extension)
    ) {
        return (
            <>
                <ForgeUIRenderer
                    accountId={accountId}
                    bridge={{ ...bridge, showFlag, closeFlag }}
                    client={apolloClient}
                    cloudId={cloudId}
                    contextIds={contextIds}
                    entryPoint={entryPoint}
                    extension={extension}
                    extensionData={extensionDataLocal}
                    extensionPayload={isForgeCustomFieldsOnPortalRequestEnabled() ? extensionPayloadLocal : undefined}
                    getContextToken={contextTokenCallback}
                    height={calculateHeight(extension.type)}
                    loadingComponent={loadingComponent}
                    locale={locale}
                    mentionProvider={Promise.resolve(getMentionProvider(accountId, requestKey))}
                    modalExtension={modalExtension}
                    product="jira"
                    // the logic below is carried over from the old renderer
                    getModalWidth={(size) => (size === 'max' ? '100%' : 'large')}
                    modalStyles={{ padding: `${token('space.025', '2px')} ${token('space.300', '24px')}` }}
                    onModalExtensionClose={onModalExtensionClose}
                    isIframeResizable={isIframeResizable}
                    customBridgeMethods={customBridgeMethods}
                    onIframeLoad={onIframeLoad}
                    isIframeHidden={isIframeHidden}
                    onConsentInitialization={
                        isForgeOnConsentInitializationPropEnabled() ? onConsentInitialization : undefined
                    }
                    onConsentSuccess={onConsentSuccess}
                    consentMessage={consentMessage}
                    components={components}
                />
                <FlagGroup onDismissed={removeFlag}>
                    {flags.map((flag) =>
                        flag.isAutoDismiss ? (
                            <AutoDismissFlag
                                key={flag.id}
                                id={flag.id || ''}
                                icon={flagIcon(flag.type)}
                                title={flag.title}
                                description={flag.description}
                                actions={flag.actions?.map((action) => ({ ...action, content: action.text }))}
                                appearance={flag.appearance}
                            />
                        ) : (
                            <Flag
                                key={flag.id}
                                id={flag.id || ''}
                                icon={flagIcon(flag.type)}
                                title={flag.title}
                                description={flag.description}
                                actions={flag.actions?.map((action) => ({ ...action, content: action.text }))}
                                appearance={flag.appearance}
                            />
                        )
                    )}
                </FlagGroup>
            </>
        );
    }

    return (
        <UIKit1Renderer
            extension={extension}
            getContextToken={contextTokenCallback}
            getComponents={getComponents}
            contextIds={contextIds}
            requestKey={requestKey}
            cloudId={cloudId}
            accountId={accountId}
            onSubmit={onSubmit}
            onRender={onRender}
            loadingComponent={loadingComponent}
            extensionData={extensionDataLocal}
            extensionPayload={isForgeCustomFieldsOnPortalRequestEnabled() ? extensionPayloadLocal : undefined}
        />
    );
};

export default Renderer;
